import {optional} from '@raison/io-ts-tools';
import {ark} from 'arktype';
import {identity} from 'effect/Function';
import {isLeft} from 'fp-ts/Either';
import * as IoTs from 'io-ts';
import {ErroneousServerResult} from './ErroneousServerResult';

type FormPrimitive = File | File[] | string | number | boolean | null | undefined | bigint;

export type ValidationFields<T> = {
    [K in keyof T]?: T[K] extends FormPrimitive ? string[] : ValidationFields<T[K]>;
};
export type ValidationFieldsCodec<T> = IoTs.Type<ValidationFields<T>>;

type Child<T> = {
    errors?: T extends object ? undefined : string[];
    children?: T extends object ? Children<T> : undefined;
};

type Children<T> = {
    [Key in keyof T]?: Child<T[Key]>;
};

export interface ServerValidationResult<TFields = never> extends ErroneousServerResult {
    fields?: TFields;
}

export namespace ServerValidationResult {
    export function createCodecForType<TFieldsType>() {
        return (fieldsCodec: ValidationFieldsCodec<TFieldsType>) => createCodec(fieldsCodec);
    }

    export function createCodec<TFields>(
        fieldsCodec: IoTs.Type<TFields>
    ): IoTs.Type<ServerValidationResult<TFields>, unknown> {
        const {validate: validateUntransformed} = IoTs.type({
            message: IoTs.string,
            code: IoTs.number,
            errors: optional(
                IoTs.type({
                    children: IoTs.type({}),
                })
            ),
        });
        const {is: isTransformed} = IoTs.type({
            message: IoTs.string,
            code: IoTs.number,
            fields: optional(fieldsCodec),
        });

        const name = 'ServerValidationResult';

        return new IoTs.Type<ServerValidationResult<TFields>, unknown>(
            name,
            isTransformed,
            (input, context) => {
                const transformable = validateUntransformed(input, context);
                if (isLeft(transformable)) {
                    return transformable;
                }
                const {code, message, errors} = transformable.right;

                if (!errors?.children) {
                    return IoTs.success({code, message});
                }

                const transformed = transform(errors.children, context);
                if (isLeft(transformed)) {
                    return transformed;
                }

                const validatedFields = fieldsCodec.validate(transformed.right, context);
                if (isLeft(validatedFields)) {
                    return validatedFields;
                }

                return IoTs.success({code, message, fields: validatedFields.right});
            },
            identity
        );
    }

    const transform = (
        errors: Children<unknown>,
        context: IoTs.Context
    ): IoTs.Validation<ValidationFields<unknown>> => {
        if (!errors || typeof errors !== 'object') return IoTs.failure(errors, context);

        const result: ValidationFields<any> = {};

        const entries = Object.entries(errors) as Array<[keyof ValidationFields<unknown>, Child<unknown>]>;
        for (const [key, child] of entries) {
            if (!child.errors && !child.children) continue;

            if (child.errors) {
                result[key] = child.errors;
                continue;
            }

            if (child.children) {
                const transformationResult = transform(child.children, context);
                if (isLeft(transformationResult)) {
                    return transformationResult;
                }
                result[key] = transformationResult.right;
            }
        }

        return IoTs.success(result);
    };

    export const flattenRaw = (result: {
        readonly errors?: {
            readonly children: {
                readonly [x: string]: {
                    readonly errors?: readonly string[];
                };
            };
        };
    }): {
        [key: string]: string | undefined;
    } => {
        if (!result.errors?.children) {
            return {};
        }
        return Object.fromEntries(
            Object.entries(result.errors.children)
                .map(([field, box]) => [field, box.errors?.[0]])
                .filter(([, error]) => !!error)
        );
    };

    const ErrorsChildren = ark.type({
        '[string]': {
            'errors?': 'string[]',
            // TODO // 'children?': () => ErrorsChildren,
        },
    });
    const ServerValidationResultArkType = ark
        .type({
            errors: {
                children: ErrorsChildren,
            },
        })
        .pipe(flattenRaw);

    export const to = ServerValidationResultArkType.to.bind(ServerValidationResultArkType);
    export const pipe = ServerValidationResultArkType.pipe.bind(ServerValidationResultArkType);
}
